var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { FormHelperText } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { getFieldLabelTranslationArgs, useInput, useResourceContext, useTranslate } from 'ra-core';
import { InputHelperText, Labeled, sanitizeInputRestProps } from 'ra-ui-materialui';
import { TiptapEditorProvider } from './TiptapEditorProvider';
import { RichTextInputToolbar } from './RichTextInputToolbar';
/**
 * A rich text editor for the react-admin that is accessible and supports translations. Based on [Tiptap](https://www.tiptap.dev/).
 * @param props The input props. Accept all common react-admin input props.
 * @param {EditorOptions} props.editorOptions The options to pass to the Tiptap editor.
 * @param {ReactNode} props.toolbar The toolbar containing the editors commands.
 *
 * @example <caption>Customizing the editors options</caption>
 * import { RichTextInput, RichTextInputToolbar } from 'ra-richtext-tiptap';
 * const MyRichTextInput = (props) => (
 *     <RichTextInput
 *         toolbar={<RichTextInputToolbar size="large" />}
 *         label="Body"
 *         source="body"
 *         {...props}
 *     />
 * );
 *
 * @example <caption>Customizing the toolbar size</caption>
 * import { RichTextInput, RichTextInputToolbar } from 'ra-richtext-tiptap';
 * const MyRichTextInput = (props) => (
 *     <RichTextInput
 *         toolbar={<RichTextInputToolbar size="large" />}
 *         label="Body"
 *         source="body"
 *         {...props}
 *     />
 * );
 *
 * @example <caption>Customizing the toolbar commands</caption>
 * import { RichTextInput, RichTextInputToolbar } from 'ra-richtext-tiptap';
 * const MyRichTextInput = ({ size, ...props }) => (
 *     <RichTextInput
 *         toolbar={(
 *             <RichTextInputToolbar>
 *                 <RichTextInputLevelSelect size={size} />
 *                 <FormatButtons size={size} />
 *                 <ListButtons size={size} />
 *                 <LinkButtons size={size} />
 *                 <QuoteButtons size={size} />
 *                 <ClearButtons size={size} />
 *             </RichTextInputToolbar>
 *         )}
 *         label="Body"
 *         source="body"
 *         {...props}
 *     />
 * );
 */
export var RichTextInput = function (props) {
    var _a = props.disabled, disabled = _a === void 0 ? false : _a, _b = props.readOnly, readOnly = _b === void 0 ? false : _b, _c = props.editorOptions, editorOptions = _c === void 0 ? DefaultEditorOptions : _c, helperText = props.helperText, _d = props.toolbar, toolbar = _d === void 0 ? React.createElement(RichTextInputToolbar, null) : _d, label = props.label, source = props.source, rest = __rest(props, ["disabled", "readOnly", "editorOptions", "helperText", "toolbar", "label", "source"]);
    var resource = useResourceContext(props);
    var classes = useStyles(props);
    var translate = useTranslate();
    var _e = useInput(props), id = _e.id, input = _e.input, isRequired = _e.isRequired, meta = _e.meta;
    var finalLabel = label
        ? translate(label, { _: label })
        : translate.apply(void 0, getFieldLabelTranslationArgs({
            label: label,
            resource: resource,
            source: source,
        }));
    var editor = useEditor(__assign(__assign({}, editorOptions), { content: input.value, editorProps: {
            attributes: __assign({ id: id, 'aria-label': finalLabel }, (disabled || readOnly ? EditorAttributesNotEditable : EditorAttributes)),
        } }));
    var error = meta.error, submitError = meta.submitError, touched = meta.touched;
    useEffect(function () {
        if (!editor)
            return;
        editor.setOptions({
            editorProps: {
                attributes: __assign({ id: id, 'aria-label': finalLabel }, (disabled || readOnly ? EditorAttributesNotEditable : EditorAttributes)),
            },
        });
    }, [disabled, editor, readOnly, finalLabel, id]);
    useEffect(function () {
        if (!editor) {
            return;
        }
        var handleEditorUpdate = function () {
            if (editor.isEmpty) {
                input.onChange('');
                input.onBlur();
                return;
            }
            var html = editor.getHTML();
            input.onChange(html);
            input.onBlur();
        };
        editor.on('update', handleEditorUpdate);
        return function () {
            editor.off('update', handleEditorUpdate);
        };
    }, [editor, input]);
    return (React.createElement(Labeled, __assign({ id: id, isRequired: isRequired, label: label, meta: meta, source: source, resource: resource }, sanitizeInputRestProps(rest)),
        React.createElement("div", null,
            React.createElement(TiptapEditorProvider, { value: editor },
                toolbar,
                React.createElement(EditorContent, { className: classes.editorContent, editor: editor })),
            React.createElement(FormHelperText, { error: Boolean(error || submitError) },
                React.createElement(InputHelperText, { touched: touched, error: error || submitError, helperText: helperText })))));
};
var EditorAttributes = {
    role: 'textbox',
    'aria-multiline': 'true',
};
var EditorAttributesNotEditable = {
    role: 'textbox',
    'aria-multiline': 'true',
    contenteditable: false,
    'aria-readonly': 'true',
};
export var DefaultEditorOptions = {
    extensions: [
        StarterKit,
        Underline,
        Link,
        TextAlign.configure({
            types: ['heading', 'paragraph'],
        }),
    ],
};
var useStyles = makeStyles(function (theme) { return ({
    root: {},
    editorContent: {
        '& > div': {
            padding: theme.spacing(1),
            borderStyle: 'solid',
            borderWidth: '1px',
            borderColor: theme.palette.type === 'light' ? 'rgba(0, 0, 0, 0.23)' : 'rgba(255, 255, 255, 0.23)',
            borderRadius: theme.shape.borderRadius,
        },
    },
}); }, {
    name: 'RaRichTextInput',
});
